import api from '../api/api';

/**
 * Service for handling attendance-related API calls
 */
const attendanceService = {
  /**
   * Get attendance records for a specific halaqa within a date range
   * @param {string} halaqaId - The ID of the halaqa
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} - Promise containing attendance records
   */
  getAttendanceForDateRange: async (halaqaId, startDate, endDate) => {
    try {
      // First try the date range specific endpoint
      try {
        const response = await api.get(`/attendance/halaqa/${halaqaId}/daterange`, {
          params: { startDate, endDate }
        });
        
        if (response.data && response.data.success) {
          const records = response.data.data || [];
          console.log(`Fetched ${records.length} attendance records for halaqa ${halaqaId}`);
          return records;
        }
        
        if (Array.isArray(response.data)) {
          console.log(`Fetched ${response.data.length} attendance records for halaqa ${halaqaId}`);
          return response.data;
        }
      } catch (err) {
        console.log('Date range endpoint not available, falling back to regular endpoint');
      }
      
      // If that doesn't work, fall back to the regular endpoint and filter manually
      const response = await api.get(`/attendance/halaqa/${halaqaId}`);
      
      let allRecords = [];
      if (response.data && response.data.success) {
        allRecords = response.data.data || [];
      } else if (Array.isArray(response.data)) {
        allRecords = response.data;
      }
      
      console.log(`Fetched ${allRecords.length} total attendance records for halaqa ${halaqaId}`);
      
      // Filter records by date range
      const startDateObj = new Date(startDate);
      startDateObj.setHours(0, 0, 0, 0); // Set to beginning of day
      
      const endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999); // Set to end of day
      
      console.log(`Filtering dates between ${startDateObj.toISOString()} and ${endDateObj.toISOString()}`);
      
      const filteredRecords = allRecords.filter(record => {
        if (!record.date) return false;
        
        const recordDate = new Date(record.date);
        const isInRange = recordDate >= startDateObj && recordDate <= endDateObj;
        
        if (isInRange) {
          console.log(`Record date ${recordDate.toISOString()} is in range`);
        }
        
        return isInRange;
      });
      
      console.log(`Filtered to ${filteredRecords.length} records in date range for halaqa ${halaqaId}`);
      return filteredRecords;
    } catch (error) {
      console.error(`Error fetching attendance for halaqa ${halaqaId} from ${startDate} to ${endDate}:`, error);
      return [];
    }
  },
  
  /**
   * Get attendance records for all halaqas within a date range (admin only)
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} - Promise containing attendance records
   */
  getAllAttendanceForDateRange: async (startDate, endDate) => {
    try {
      // This endpoint might not exist yet in the backend, so we'll simulate it by
      // fetching attendance for each halaqa and combining the results
      const halaqasResponse = await api.get('/halaqas');
      let halaqas = [];
      
      if (halaqasResponse.data && halaqasResponse.data.success) {
        halaqas = halaqasResponse.data.data || [];
      } else if (Array.isArray(halaqasResponse.data)) {
        halaqas = halaqasResponse.data;
      }
      
      console.log('Fetched halaqas for attendance:', halaqas.length);
      
      if (halaqas.length === 0) {
        console.log('No halaqas found to fetch attendance for');
        return [];
      }
      
      // Fetch attendance for each halaqa in parallel
      const attendancePromises = halaqas.map(halaqa => {
        return api.get(`/attendance/halaqa/${halaqa._id}`, {
          params: { startDate, endDate }
        }).then(response => {
          if (response.data && response.data.success) {
            return response.data.data || [];
          }
          return response.data || [];
        }).catch(err => {
          console.error(`Error fetching attendance for halaqa ${halaqa._id}:`, err);
          return [];
        });
      });
      
      const attendanceResults = await Promise.all(attendancePromises);
      
      // Flatten the results from all halaqas
      const allAttendance = attendanceResults.flat();
      console.log(`Fetched ${allAttendance.length} attendance records across all halaqas`);
      
      // Apply date filtering to combined results
      const startDateObj = new Date(startDate);
      startDateObj.setHours(0, 0, 0, 0); // Set to beginning of day
      
      const endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999); // Set to end of day
      
      console.log(`Filtering ALL records between ${startDateObj.toISOString()} and ${endDateObj.toISOString()}`);
      
      const filteredRecords = allAttendance.filter(record => {
        if (!record.date) return false;
        
        const recordDate = new Date(record.date);
        return recordDate >= startDateObj && recordDate <= endDateObj;
      });
      
      console.log(`After date filtering: ${filteredRecords.length} records match the date range`);
      
      return filteredRecords;
    } catch (error) {
      console.error(`Error fetching all attendance from ${startDate} to ${endDate}:`, error);
      return [];
    }
  },
  
  /**
   * Export attendance records to CSV
   * @param {string} halaqaId - The ID of the halaqa or 'all' for all classes
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} - Promise containing CSV data
   */
  exportAttendanceToCSV: async (halaqaId, startDate, endDate) => {
    try {
      console.log(`Exporting attendance CSV for halaqaId=${halaqaId}, startDate=${startDate}, endDate=${endDate}`);
      
      // Check if the export endpoint exists
      const endpoint = halaqaId === 'all' 
        ? '/attendance/export/csv'
        : `/attendance/halaqa/${halaqaId}/export/csv`;
      
      try {
        // First try with the dedicated export endpoint
        const response = await api.get(endpoint, {
          params: { startDate, endDate },
          responseType: 'blob'
        });
        
        console.log('Successfully exported attendance data using export endpoint');
        return response;
      } catch (err) {
        console.log('Export endpoint not available, generating CSV manually');
        
        // Fetch the data and create a CSV manually
        let attendanceData;
        if (halaqaId === 'all') {
          attendanceData = await this.getAllAttendanceForDateRange(startDate, endDate);
        } else {
          attendanceData = await this.getAttendanceForDateRange(halaqaId, startDate, endDate);
        }
        
        // Generate CSV data
        let csvContent = 'Date,Student,Class,Status\n';
        
        attendanceData.forEach(record => {
          const date = record.date ? new Date(record.date).toLocaleDateString() : 'Unknown';
          
          // Get student name (handling different data structures)
          let studentName = 'Unknown Student';
          if (typeof record.studentId === 'object' && record.studentId && record.studentId.name) {
            studentName = record.studentId.name;
          } else if (record.studentName) {
            studentName = record.studentName;
          }
          
          // Get class name (handling different data structures)
          let className = 'Unknown Class';
          if (typeof record.halaqaId === 'object' && record.halaqaId && record.halaqaId.name) {
            className = record.halaqaId.name;
          } else if (record.halaqaName) {
            className = record.halaqaName;
          } else if (record.className) {
            className = record.className;
          }
          
          const status = record.status || 'unknown';
          
          // Escape fields to handle commas, quotes, etc.
          const escapeCsvField = (field) => {
            if (field.includes(',') || field.includes('"') || field.includes('\n')) {
              return `"${field.replace(/"/g, '""')}"`;  // Double quotes for CSV escaping
            }
            return field;
          };
          
          csvContent += `${escapeCsvField(date)},${escapeCsvField(studentName)},${escapeCsvField(className)},${escapeCsvField(status)}\n`;
        });
        
        // Create response-like object
        const blob = new Blob([csvContent], { type: 'text/csv' });
        return { data: blob };
      }
    } catch (error) {
      console.error('Error exporting attendance data:', error);
      throw error;
    }
  },
  /**
   * Get attendance records for a specific date and halaqa
   * @param {string} halaqaId - The ID of the halaqa
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise} - Promise containing attendance records
   */
  getAttendanceByHalaqaAndDate: async (halaqaId, date) => {
    try {
      const response = await api.get(`/attendance/halaqa/${halaqaId}/date/${date}`);
      
      // Handle different response structures
      if (response.data && response.data.success) {
        return response.data.data || [];
      }
      
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching attendance for halaqa ${halaqaId} on ${date}:`, error);
      throw error;
    }
  },

  /**
   * Get attendance records for a specific halaqa (all dates)
   * @param {string} halaqaId - The ID of the halaqa
   * @returns {Promise} - Promise containing attendance records
   */
  getAttendanceByHalaqa: async (halaqaId) => {
    try {
      const response = await api.get(`/attendance/halaqa/${halaqaId}`);
      
      if (response.data && response.data.success) {
        return response.data.data || [];
      }
      
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching attendance for halaqa ${halaqaId}:`, error);
      throw error;
    }
  },

  /**
   * Get attendance records for a specific student
   * @param {string} studentId - The ID of the student
   * @returns {Promise} - Promise containing attendance records
   */
  getStudentAttendance: async (studentId) => {
    try {
      const response = await api.get(`/attendance/student/${studentId}`);
      
      if (response.data && response.data.success) {
        return response.data.data || [];
      }
      
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching attendance for student ${studentId}:`, error);
      throw error;
    }
  },

  /**
   * Mark attendance for a single student
   * @param {object} attendanceData - Object containing attendance details
   * @returns {Promise} - Promise containing the created/updated attendance record
   */
  markAttendance: async (attendanceData) => {
    try {
      const response = await api.post('/attendance', attendanceData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error marking attendance:', error);
      throw error;
    }
  },

  /**
   * Mark attendance for multiple students in a halaqa
   * @param {string} halaqaId - The ID of the halaqa
   * @param {Array} attendanceRecords - Array of attendance objects with studentId and status
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} centerId - Optional center ID parameter
   * @returns {Promise} - Promise containing the created/updated attendance records
   */
  markBulkAttendance: async (halaqaId, attendanceRecords, date, centerId = null) => {
    try {
      const payload = {
        halaqaId,
        attendance: attendanceRecords,
        date
      };
      
      // Add center ID if provided
      if (centerId) {
        payload.center = centerId;
      }
      
      const response = await api.post('/attendance/bulk', payload);
      
      if (response.data && response.data.success) {
        return response.data.data || [];
      }
      
      return response.data || [];
    } catch (error) {
      console.error('Error marking bulk attendance:', error);
      throw error;
    }
  },

  /**
   * Update an existing attendance record
   * @param {string} attendanceId - The ID of the attendance record
   * @param {object} updateData - Object containing fields to update
   * @returns {Promise} - Promise containing the updated attendance record
   */
  updateAttendance: async (attendanceId, updateData) => {
    try {
      const response = await api.put(`/attendance/${attendanceId}`, updateData);
      
      if (response.data && response.data.success) {
        return response.data.data || {};
      }
      
      return response.data || {};
    } catch (error) {
      console.error(`Error updating attendance ${attendanceId}:`, error);
      throw error;
    }
  }
};

export default attendanceService;